<template>
  <main class="glide-page">
    <section class="hero">
      <picture>
        <source media="(max-width: 899px), (orientation: portrait)" :srcset="phoneHero" />
        <img :src="webHero" :alt="$t('STERiGLIDE.name')" class="hero-image" />
      </picture>
      <div class="hero-copy">
        <h1 v-html="$t('STERiGLIDE.s1.title')"></h1>
        <p class="hero-name" v-html="$t('STERiGLIDE.s1.body')"></p>
        <p class="hero-license" v-html="$t('STERiGLIDE.s1.body1')"></p>
      </div>
    </section>

    <section class="intro-section">
      <div class="content-width intro-copy">
        <h2 v-html="$t('STERiGLIDE.public.introTitle')"></h2>
        <p v-html="$t('STERiGLIDE.public.introBody')"></p>
      </div>
    </section>

    <section class="selector-section content-width">
      <div class="selector-stage">
        <div class="mobile-selector-heading">
          <h2 v-html="$t('STERiGLIDE.s3.title1')"></h2>
          <p v-html="$t('STERiGLIDE.s3.title2')"></p>
        </div>

        <div class="selector-visual">
          <picture>
            <source media="(max-width: 899px), (orientation: portrait)" :srcset="currentPhoneImage" />
            <img :src="currentWebImage" :alt="$t('STERiGLIDE.s3.title1')" />
          </picture>
          <aside class="selector-panel">
            <div class="selector-heading">
              <h2 v-html="$t('STERiGLIDE.s3.title1')"></h2>
              <p v-html="$t('STERiGLIDE.s3.title2')"></p>
            </div>
            <div class="selector-buttons">
              <button
                v-for="button in selectorButtons"
                :key="button.id"
                type="button"
                :class="{ active: selectedArea === button.id }"
                @click="selectedArea = button.id"
                v-html="$t(button.label)"
              ></button>
            </div>
            <p
              :class="[
                'selector-reference-note selector-reference-note--desktop',
                { 'is-full-face': selectedArea === 1 }
              ]"
              v-html="$t('STERiGLIDE.public.referenceNote')"
            ></p>
          </aside>
        </div>

        <div v-if="selectedMobileSpecs.length" class="mobile-selector-specs">
          <div class="mobile-specs-inner">
            <div
              class="mobile-specs-part"
              :style="{ gridRowEnd: `span ${selectedMobileSpecs.length}` }"
              v-html="$t(selectedButton.label)"
            ></div>
            <template v-for="(item, index) in selectedMobileSpecs" :key="`${item.gauge}-${item.text}-${index}`">
              <div class="mobile-specs-divider">｜</div>
              <div class="mobile-specs-row">
                <span :class="['gauge-pill', gaugePillClass(item.gauge)]">{{ item.gauge }}</span>
                <span>{{ item.text }}</span>
              </div>
            </template>
          </div>
          <p
            class="selector-reference-note selector-reference-note--mobile"
            v-html="$t('STERiGLIDE.public.referenceNote')"
          ></p>
        </div>
      </div>
    </section>

    <section class="feature-section content-width">
      <article v-for="feature in features" :key="feature.title" class="feature-card">
        <h3 v-html="$t(feature.title)"></h3>
        <p v-html="$t(feature.body)"></p>
        <picture>
          <source media="(max-width: 899px)" :srcset="feature.phoneImage" />
          <img :src="feature.webImage" :alt="$t(feature.title)" loading="lazy" />
        </picture>
      </article>
    </section>

    <section class="product-showcase">
      <div class="product-copy">
        <h2 v-html="$t('STERiGLIDE.title')"></h2>
        <p v-html="$t('STERiGLIDE.description')"></p>
      </div>
    </section>

    <section class="spec-section">
      <div class="spec-inner">
        <h2 v-html="$t('model')"></h2>
        <div class="table-scroller">
          <table class="spec-table">
            <thead>
              <tr>
                <th scope="col">Gauge</th>
                <th scope="col">Code</th>
                <th scope="col">Description</th>
                <th scope="col">Inch</th>
                <th scope="col">Thin Wall</th>
                <th scope="col">Box</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in groupedData" :key="group.gauge">
                <tr v-for="(item, itemIndex) in group.items" :key="item.code">
                  <td v-if="itemIndex === 0" :rowspan="group.items.length" class="gauge-cell">
                    {{ group.gauge }}
                  </td>
                  <td>{{ item.code }}</td>
                  <td>{{ item.description }}</td>
                  <td>{{ item.inch }}</td>
                  <td>{{ item.utw }}</td>
                  <td>{{ item.box }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <p class="legal-note">
          {{ $t('STERiGLIDE.public.operator') }}<br />
          {{ $t('STERiGLIDE.s1.body1') }}
        </p>
        <a class="catalogue-link" href="/Brochure/TSK_STERiGLIDE.pdf" target="_blank">
          <span>{{ $t('STERiGLIDE.public.catalogue') }}</span>
        </a>
      </div>
    </section>
  </main>

  <ContactFooter />
</template>

<script>
import ContactFooter from '@/components/ContactFooter.vue'

export default {
  name: 'PublicSTERiGLIDE',
  components: { ContactFooter },
  data() {
    return {
      webHero: require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_01.jpg'),
      phoneHero: require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_01.jpg'),
      selectedArea: 1,
      mobileQuery: null,
      mobileQueryHandler: null,
      selectorButtons: [
        { id: 1, label: 'STERiGLIDE.s3.k1', image: null },
        { id: 2, label: 'STERiGLIDE.s3.k2', image: '01' },
        { id: 3, label: 'STERiGLIDE.s3.k3', image: '02' },
        { id: 4, label: 'STERiGLIDE.s3.k4', image: '03' },
        { id: 5, label: 'STERiGLIDE.s3.k5', image: '04' },
        { id: 6, label: 'STERiGLIDE.s3.k6', image: '05' },
        { id: 7, label: 'STERiGLIDE.s3.k7', image: '06' },
        { id: 8, label: 'STERiGLIDE.s3.k8', image: '07' },
        { id: 9, label: 'STERiGLIDE.s3.k9', image: '08' },
        { id: 10, label: 'STERiGLIDE.s3.k10', image: '09' },
        { id: 11, label: 'STERiGLIDE.s3.k11', image: '10' },
        { id: 12, label: 'STERiGLIDE.s3.k12', image: '11' },
        { id: 13, label: 'STERiGLIDE.s3.k13', image: '12' },
        { id: 14, label: 'STERiGLIDE.s3.k14', image: '13' }
      ],
      mobileSpecs: {
        2: [{ gauge: '25G', text: '38mm/50mm' }],
        3: [{ gauge: '22G', text: '50mm/70mm' }, { gauge: '25G', text: '38mm/50mm' }],
        4: [{ gauge: '25G', text: '38mm/50mm' }],
        5: [{ gauge: '25G', text: '38mm/50mm' }, { gauge: '30G', text: '25mm' }],
        6: [{ gauge: '22G', text: '50mm/70mm' }, { gauge: '25G', text: '50mm' }],
        7: [{ gauge: '22G', text: '38mm/50mm' }, { gauge: '25G', text: '38mm/50mm' }],
        8: [{ gauge: '25G', text: '38mm/50 mm' }],
        9: [{ gauge: '22G', text: '50mm/70mm' }, { gauge: '25G', text: '50mm' }],
        10: [{ gauge: '25G', text: '38mm/50mm' }, { gauge: '27G', text: '38mm/50mm' }],
        11: [{ gauge: '25G', text: '38mm' }, { gauge: '27G', text: '25mm/38mm/50mm' }, { gauge: '30G', text: '25mm' }],
        12: [{ gauge: '25G', text: '38mm/50mm' }],
        13: [{ gauge: '22G', text: '38mm/50mm/70mm' }, { gauge: '25G', text: '38mm/50mm' }],
        14: [{ gauge: '25G', text: '38mm/50mm' }]
      },
      features: [
        {
          title: 'STERiGLIDE.public.features.title1', body: 'STERiGLIDE.public.features.body1',
          webImage: require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_04_01.jpg'),
          phoneImage: require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_04_01.jpg')
        },
        {
          title: 'STERiGLIDE.public.features.title2', body: 'STERiGLIDE.public.features.body2',
          webImage: require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_04_02.jpg'),
          phoneImage: require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_04_02.jpg')
        },
        {
          title: 'STERiGLIDE.public.features.title3', body: 'STERiGLIDE.public.features.body3',
          webImage: require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_04_03.jpg'),
          phoneImage: require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_04_03.jpg')
        }
      ],
      tableData: [
        { gauge: '30G', code: 'SGC-30025', description: 'STERiGLIDE Cannula 30G x 25mm', inch: '1″', utw: '√', box: '20' },
        { gauge: '27G', code: 'SGC-27025', description: 'STERiGLIDE Cannula 27G x 25mm', inch: '1″', utw: '√', box: '20' },
        { gauge: '27G', code: 'SGC-27038', description: 'STERiGLIDE Cannula 27G x 38mm', inch: '1½″', utw: '√', box: '20' },
        { gauge: '27G', code: 'SGC-27050', description: 'STERiGLIDE Cannula 27G x 50mm', inch: '2″', utw: '√', box: '20' },
        { gauge: '25G', code: 'SGC-25038', description: 'STERiGLIDE Cannula 25G x 38mm', inch: '1½″', utw: '√', box: '20' },
        { gauge: '25G', code: 'SGC-25050', description: 'STERiGLIDE Cannula 25G x 50mm', inch: '2″', utw: '√', box: '20' },
        { gauge: '22G', code: 'SGC-22038', description: 'STERiGLIDE Cannula 22G x 38mm', inch: '1½″', utw: '√', box: '20' },
        { gauge: '22G', code: 'SGC-22050', description: 'STERiGLIDE Cannula 22G x 50mm', inch: '2″', utw: '√', box: '20' },
        { gauge: '22G', code: 'SGC-22070', description: 'STERiGLIDE Cannula 22G x 70mm', inch: '2¾″', utw: '√', box: '20' }
      ]
    }
  },
  computed: {
    selectedButton() {
      return this.selectorButtons.find(button => button.id === this.selectedArea)
    },
    currentWebImage() {
      return this.selectorImage('web')
    },
    currentPhoneImage() {
      return this.selectorImage('phone')
    },
    selectedMobileSpecs() {
      return this.mobileSpecs[this.selectedArea] || []
    },
    groupedData() {
      return this.tableData.reduce((groups, item) => {
        const group = groups.find(entry => entry.gauge === item.gauge)
        if (group) group.items.push(item)
        else groups.push({ gauge: item.gauge, items: [item] })
        return groups
      }, [])
    }
  },
  methods: {
    selectorImage(size) {
      const suffix = this.selectedButton && this.selectedButton.image
        ? `_${this.selectedButton.image}`
        : ''
      return require(`@/assets/images/AppShell/products/STERiGLIDE/${size}/STERiGLIDE_02${suffix}.jpg`)
    },
    gaugePillClass(gauge) {
      return `g${String(gauge).replace(/\D/g, '')}`
    }
  },
  mounted() {
    if (typeof window === 'undefined' || !window.matchMedia) return

    this.mobileQuery = window.matchMedia('(max-width: 899px), (orientation: portrait)')
    this.mobileQueryHandler = event => {
      if (event.matches && this.selectedArea === 1) this.selectedArea = 2
    }
    this.mobileQueryHandler(this.mobileQuery)

    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener('change', this.mobileQueryHandler)
    } else {
      this.mobileQuery.addListener(this.mobileQueryHandler)
    }
  },
  beforeUnmount() {
    if (!this.mobileQuery || !this.mobileQueryHandler) return

    if (this.mobileQuery.removeEventListener) {
      this.mobileQuery.removeEventListener('change', this.mobileQueryHandler)
    } else {
      this.mobileQuery.removeListener(this.mobileQueryHandler)
    }
  }
}
</script>

<style scoped>
.glide-page {
  --glide-blue: #0071bb;
  width: min(calc(100% - 3rem), 1320px);
  margin: 0 auto;
  overflow: hidden;
  color: #666;
  font-size: 16px;
}

.content-width,
.spec-inner {
  width: min(calc(100% - 3rem), 1320px);
  margin: 0 auto;
}

.glide-page h1,
.glide-page h2 {
  margin-top: 0;
  color: var(--glide-blue);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.45;
}

.glide-page h3 {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.45;
}

.glide-page p {
  font-size: 16px;
  line-height: 1.9;
}

.hero { position: relative; line-height: 0; }
.hero-image { display: block; width: 100%; height: auto; }

.hero-copy {
  position: absolute;
  top: 50%;
  left: 41%;
  width: min(36%, 520px);
  transform: translateY(-50%);
  color: #fff;
  text-align: center;
}

.hero-copy h1 { margin-bottom: 0.5rem; color: #fff !important; }
.hero-name { margin: 0; color: #fff !important; font-size: 20px !important; }
.hero-license { margin: 1.1rem 0 0; color: #fff !important; }

.intro-section { padding: 4rem 0; background: #f7f7f7; text-align: center; }
.intro-copy h2 { margin-bottom: 0.75rem; color: #0071bb !important; }
.intro-copy p { max-width: 72ch; margin: 0 auto; }

.selector-section { width: 100%; padding: 0 0 2rem; }
.selector-stage { position: relative; }
.selector-visual { position: relative; }
.selector-visual img { display: block; width: 100%; height: auto; }
.mobile-selector-heading,
.mobile-selector-specs { display: none; }

.selector-panel {
  position: absolute;
  top: 50%;
  left: 3rem;
  width: min(38%, 450px);
  padding: 1.25rem;
  transform: translateY(-50%);
  border-radius: 8px;
  background: rgba(247, 247, 247, 0.96);
  box-shadow: 0 3px 16px rgba(0, 0, 0, 0.08);
}

.selector-heading {
  margin: -1.25rem -1.25rem 1rem;
  padding: 1rem 1.25rem;
  border-radius: 8px 8px 0 0;
  background: var(--glide-blue);
  color: #fff;
}

.selector-heading h2,
.selector-heading p { margin: 0; color: #fff !important; }
.selector-heading h2 { font-size: 20px; }
.selector-heading p { font-size: 14px; line-height: 1.5; }

.selector-buttons {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.selector-buttons button {
  min-height: 38px;
  padding: 0.45rem 0.65rem;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
}

.selector-buttons button:hover,
.selector-buttons button:focus-visible {
  border-color: var(--glide-blue);
  background: var(--glide-blue);
  color: #fff;
}

.selector-buttons button.active {
  border-color: transparent;
  background: linear-gradient(90deg, #0071bb, #00b5ef);
  color: #fff;
}

.selector-reference-note {
  font-size: 13px !important;
  font-weight: 400;
  line-height: 1.7 !important;
  text-align: left;
}

.selector-reference-note--desktop {
  position: absolute;
  top: calc(100% + 1rem);
  left: 0;
  width: 100%;
  margin: 0;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

.selector-reference-note--desktop.is-full-face {
  color: #000;
  text-shadow: none;
}

.selector-reference-note--mobile { display: none; }

.feature-section {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2rem;
  padding-top: 2rem;
  padding-bottom: 4rem;
}

.feature-card { display: flex; flex-direction: column; text-align: center; }

.feature-card h3 {
  min-height: 78px;
  margin: 0 0 1rem;
  padding: 0.7rem 1rem;
  border-radius: 999px;
  background: linear-gradient(90deg, #0071bb, #00b5ef);
  color: #fff !important;
}

.feature-card p { min-height: 120px; margin: 0 0 1rem; }
.feature-card img { display: block; width: 70%; height: auto; margin: auto auto 0; }

.product-showcase {
  position: relative;
  min-height: 480px;
  background: #eefaff url('~@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_05.jpg') no-repeat center/cover;
}

.product-copy {
  position: absolute;
  top: 50%;
  left: 56%;
  width: min(38%, 560px);
  transform: translateY(-50%);
  text-align: left;
}

.product-copy h2 {
  margin-bottom: 1.5rem;
  color: #0071bb !important;
}
.product-copy p { margin: 0; color: #555; }

.spec-section { padding: 4rem 0; background: #fff; }
.spec-inner { max-width: 1120px; }
.spec-inner > h2 {
  margin-bottom: 1rem;
  color: #0071bb !important;
  text-align: left;
}
.table-scroller { overflow-x: auto; -webkit-overflow-scrolling: touch; }

.spec-table {
  width: 100%;
  min-width: 760px;
  border-collapse: separate;
  border-spacing: 0 5px;
  color: #333;
  font-size: 16px;
}

.spec-table th,
.spec-table td {
  padding: 0.8rem;
  border-right: 1px solid #e2e2e2;
  text-align: center;
  vertical-align: middle;
}

.spec-table thead th,
.gauge-cell { background: #50a7df; color: #fff; font-weight: 700; }

.spec-table tbody td:not(.gauge-cell) {
  border-top: 1px solid #dfe3e6;
  border-bottom: 1px solid #dfe3e6;
  background: #fff;
}

.legal-note {
  margin: 2rem 0 1.5rem;
  color: #222;
  font-weight: 700;
  text-align: center;
}

.catalogue-link {
  display: block;
  width: fit-content;
  min-width: 280px;
  margin: 0 auto;
  padding: 0.65rem 1.5rem;
  border: 2px solid transparent;
  border-radius: 999px;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(90deg, #0071bb, #00b5ef) border-box;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  text-decoration: none;
}

.catalogue-link span {
  background: linear-gradient(90deg, #0071bb, #00b5ef);
  background-clip: text;
  color: transparent;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.catalogue-link:hover,
.catalogue-link:focus-visible {
  background: linear-gradient(90deg, #0071bb, #00b5ef) border-box;
}

.catalogue-link:hover span,
.catalogue-link:focus-visible span {
  background: none;
  color: #fff;
  -webkit-text-fill-color: #fff;
}

@media (max-width: 899px), (orientation: portrait) {
  .glide-page { width: 100%; }

  .content-width,
  .spec-inner { width: calc(100% - 2rem); }

  .hero { min-height: 520px; background: #56a9d7; }
  .hero-image { width: 100%; height: 100%; min-height: 520px; object-fit: cover; }

  .hero-copy {
    top: 10%;
    left: 50%;
    width: min(88%, 440px);
    transform: translateX(-50%);
    text-align: center;
  }

  .intro-section { padding: 3rem 0; }
  .selector-section { width: 100%; padding-top: 0; }
  .selector-stage { display: block; }

  .mobile-selector-heading {
    display: block;
    width: 100%;
    padding: 1rem;
    background: var(--glide-blue);
    color: #fff;
    text-align: center;
  }

  .mobile-selector-heading h2,
  .mobile-selector-heading p {
    margin: 0;
    color: #fff !important;
  }

  .mobile-selector-heading h2 { font-size: clamp(16px, 4.6vw, 20px); }
  .mobile-selector-heading p { font-size: clamp(14px, 4vw, 18px); line-height: 1.35; }

  .selector-panel {
    position: absolute;
    top: 50%;
    left: 8px;
    z-index: 2;
    width: clamp(70px, 42vw, 140px);
    padding: 8px;
    max-height: 96%;
    overflow-y: auto;
    transform: translateY(-50%);
    border-radius: 10px;
    background: rgba(246, 246, 246, 0.98);
    backdrop-filter: saturate(120%) blur(2px);
  }

  /* 自訂捲軸樣式，讓它不明顯 */
  .selector-panel::-webkit-scrollbar { width: 4px; }
  .selector-panel::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }

  .selector-panel .selector-heading { display: none; }
  .selector-buttons { grid-template-columns: 1fr; gap: 6px; }
  .selector-buttons button { min-height: 0; padding: 3px; font-size: clamp(12px, 3.5vw, 14px); line-height: 1.2; }
  .selector-buttons button:first-child { display: none; }

  .mobile-selector-specs {
    display: block;
    padding: 1rem 0 1.125rem;
    background: #f0f0f0;
    color: #000;
  }

  .mobile-specs-inner {
    display: grid;
    grid-template-columns: 1fr 14px 1fr;
    align-items: center;
    width: 100%;
    padding: 0 1rem;
    column-gap: 14px;
    row-gap: 10px;
  }

  .selector-reference-note--desktop { display: none; }

  .selector-reference-note--mobile {
    display: block;
    width: calc(100% - 2rem);
    margin: 1.5rem auto 0;
    color: #444;
    font-size: 14px !important;
    line-height: 1.75 !important;
  }

  .mobile-specs-part {
    justify-self: end;
    padding-right: 8px;
    font-size: clamp(16px, 5vw, 20px);
    font-weight: 800;
    line-height: 1.3;
    text-align: right;
  }

  .mobile-specs-divider {
    width: 14px;
    font-size: clamp(18px, 6vw, 22px);
    font-weight: 800;
    line-height: 1;
    text-align: center;
  }

  .mobile-specs-row {
    display: flex;
    align-items: center;
    justify-self: start;
    gap: 10px;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
  }

  .gauge-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 56px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    color: #fff;
    font-size: 14px;
    font-weight: 800;
  }

  .gauge-pill.g22 { background: #595757; }
  .gauge-pill.g25 { background: #df6623; }
  .gauge-pill.g27 { background: #b5b5b6; }
  .gauge-pill.g30 { background: #8bbadf; }

  .feature-section { grid-template-columns: 1fr; gap: 3rem; }
  .feature-card { max-width: 520px; margin: 0 auto; }
  .feature-card h3,
  .feature-card p { min-height: 0; }

  .product-showcase {
    min-height: 720px;
    background-image: url('~@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_05.jpg');
    background-position: center top;
  }

  .product-copy {
    top: 5%;
    bottom: auto;
    left: 50%;
    width: calc(100% - 3rem);
    transform: translateX(-50%);
    text-align: center;
  }

  .product-copy h2 {
    margin-bottom: 0.75rem;
    color: #0071bb !important;
  }

  .product-copy p { line-height: 1.65; }
}

@media (max-width: 575px) {
  .hero,
  .hero-image { min-height: 460px; }
  .selector-buttons { grid-template-columns: 1fr; }
  .product-showcase { min-height: 620px; }
  .catalogue-link { min-width: 0; width: 100%; }
}
</style>
